import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import { Header } from './Header';
import { SEO } from './SEO';

interface TermsProps {
  onContactClick: (e: React.MouseEvent) => void;
  onAuthClick: () => void;
  onDemoClick: () => void;
}

export function Terms({ onContactClick, onAuthClick, onDemoClick }: TermsProps) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-black to-zinc-900">
      <SEO 
        title="Terms and Conditions - RestroCalls.AI"
        description="Terms and conditions for SMS communications and service usage for RestroCalls.AI restaurant phone management system."
        path="/terms"
      />
      <Header 
        onContactClick={onContactClick}
        onAuthClick={onAuthClick}
        onDemoClick={onDemoClick}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="mb-8">
          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>

        <div className="bg-zinc-900/50 rounded-2xl p-8 border border-zinc-800">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center">
              <FileText className="w-6 h-6 text-emerald-400" />
            </div>
            <h1 className="text-3xl font-bold text-white">Terms and Conditions</h1>
          </div>

          <div className="prose prose-invert prose-emerald max-w-none">
            <div className="text-zinc-300 leading-relaxed space-y-6">
              <p>
                By opting into SMS communications from RestroCalls.AI, you agree to receive text messages related to your orders, menu information, promotions, or customer support from our automated system.
              </p>

              <p>
                Message frequency may vary depending on user activity.
              </p>

              <p>
                Message and data rates may apply.
              </p>

              <p>
                You can opt out at any time by replying <strong className="text-emerald-400">STOP</strong>.
              </p>

              <p>
                For help, reply <strong className="text-emerald-400">HELP</strong> or contact{' '}
                <a 
                  href="mailto:support@restrocalls.ai" 
                  className="text-emerald-400 hover:text-emerald-300 transition-colors"
                >
                  support@restrocalls.ai
                </a>
              </p>

              <p>
                Use of our service constitutes acceptance of these terms. For more information, visit our website or contact support.
              </p>

              <div className="mt-12 pt-6 border-t border-zinc-700">
                <p className="text-sm text-zinc-500">
                  Last updated: 6/6/2025
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black text-zinc-400 py-16 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-12">
            <div>
              <div className="flex items-center space-x-2 mb-6">
                <div className="relative w-8 h-8 bg-zinc-900 rounded-full flex items-center justify-center">
                  <div className="w-4 h-4 bg-emerald-400 rounded-full"></div>
                </div>
                <span className="font-bold text-white">RestroCalls.AI</span>
              </div>
              <p className="text-sm leading-relaxed">
                Revolutionizing restaurant phone service with intelligent AI solutions.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Solutions</h4>
              <ul className="space-y-3 text-sm">
                <li className="hover:text-emerald-400 transition cursor-pointer">Call Handling</li>
                <li className="hover:text-emerald-400 transition cursor-pointer">Reservation Management</li>
                <li className="hover:text-emerald-400 transition cursor-pointer">Order Processing</li>
                <li className="hover:text-emerald-400 transition cursor-pointer">POS Integration</li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-3 text-sm">
                <li className="hover:text-emerald-400 transition cursor-pointer">About Us</li>
                <li className="hover:text-emerald-400 transition cursor-pointer">Case Studies</li>
                <li><Link to="/blog" className="hover:text-emerald-400 transition cursor-pointer">Blog</Link></li>
                <li><Link to="/terms" className="hover:text-emerald-400 transition cursor-pointer">Terms</Link></li>
                <li><Link to="/privacy" className="hover:text-emerald-400 transition cursor-pointer">Privacy</Link></li>
                <li>
                  <button
                    onClick={onContactClick}
                    className="hover:text-emerald-400 transition cursor-pointer"
                  >
                    Contact
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Contact</h4>
              <ul className="space-y-3 text-sm">
                <li>support@restrocalls.ai</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-zinc-800 mt-16 pt-8 text-sm text-center">
            © 2025 RestroCalls.AI. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}